export type Category = 'dispositivos' | 'liquidos' | 'desechables' | 'accesorios';

import { supplierBrands, supplierProducts } from './supplierProducts';
import { bangSupplierProducts } from './bangSupplierProducts';
import { baseUnitPriceCents, toEuros } from '../lib/pricing';

// scripts/export-catalog.mjs empaqueta este módulo para volcar el catálogo del
// servidor, y necesita los tramos de volumen; se los damos desde aquí para no
// empaquetar src/lib/pricing.ts por separado.
export { priceTiersFor } from '../lib/pricing';

export interface Brand {
  slug: string;
  label: string;
  description: string;
  image: string;
  /**
   * Color de fondo propio para la tarjeta de esta marca, en lugar del beige por defecto.
   * Se usa cuando el logo trae su propio recuadro de color y sobre el beige se vería
   * la costura, o cuando el logo es claro y necesita un fondo oscuro para leerse.
   */
  cardBackground?: string;
}

export const brands: Brand[] = [
  {
    slug: 'vapsolo',
    label: 'VapSolo',
    description: 'Dispositivos de gran autonomía, pensados para un vapeo estable y de sabor intenso.',
    image: '/images/brands/vapsolo.png',
  },
  {
    slug: 'bang',
    label: 'Bang',
    description: 'Rendimiento extremo para quienes priorizan caladas, potencia y variedad de sabores.',
    image: '/images/brands/bang-vape-logo.png.webp',
  },
  {
    slug: 'fizzy',
    label: 'Fizzy',
    description: 'Línea equilibrada con buen golpe de sabor y formatos cómodos para el día a día.',
    image: '/images/brands/Fizzy-Vape-300x110.jpg',
    cardBackground: '#000000',
  },
  {
    slug: 'waspe',
    label: 'Waspe',
    description: 'Modelos premium orientados a autonomía alta y experiencia multisabor.',
    image: '/images/brands/waspe-logo-removebg-preview.webp',
  },
  {
    slug: 'zooy',
    label: 'Zooy',
    description: 'Vapers versátiles con formatos modernos para quienes buscan vapor denso y constancia.',
    image: '/images/brands/logo.webp',
  },
  {
    slug: 'lost-mary',
    label: 'Lost Mary',
    description: 'Marca premium con formatos compactos, sabores definidos y experiencia estable de principio a fin.',
    image: '/images/brands/lost-mary-logo.png.webp',
  },
  ...supplierBrands,
];

export function slugifyBrand(brand: string) {
  return brand
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
}

export interface Product {
  id: string;
  slug: string;
  name: string;
  brand: string;
  category: Category;
  categoryLabel: string;
  /** Caladas declaradas por el fabricante. Decide la tarifa: ver src/lib/pricing.ts. */
  puffs?: number;
  price?: number;
  priceIsTemporary?: boolean;
  availableForSale?: boolean;
  availabilityMessage?: string;
  supplierReference?: string;
  emoji: string;
  image?: string;
  shortDescription: string;
  description: string;
  highlights: string[];

  nicotineMgMl: number;
  nicotineOptionsMgMl?: number[];
  capacityMl?: number;
  ingredients: string[];
  usageInstructions: string[];
  adverseEffects: string[];
  healthWarning: string;
  ageRestriction: '+18';
  countryOfOrigin: string;
  certifications: string[];
  batchTraceability: string;

  variants?: { label: string; value: string; image?: string }[];

  metaTitle: string;
  metaDescription: string;
  seoKeywords: string[];
  faq: { question: string; answer: string }[];
}

const STANDARD_WARNING =
  'Este producto contiene nicotina, una sustancia muy adictiva. Prohibida su venta a menores de 18 años. Mantener fuera del alcance de niños y mascotas.';

const HIGH_NIC_WARNING =
  'Este producto contiene nicotina en concentración alta, una sustancia muy adictiva. Uso exclusivo para adultos fumadores o vapeadores. Prohibida su venta a menores de 18 años.';

const STANDARD_ADVERSE = [
  'Irritación de boca o garganta',
  'Sequedad bucal',
  'Tos ocasional en usuarios sensibles',
  'Mareo o náuseas por uso intensivo de nicotina',
];

const DISPOSABLE_USAGE = [
  'Carga completamente el dispositivo antes del primer uso para obtener el mejor rendimiento.',
  'Inhala de forma suave y constante para mantener estabilidad de sabor y vapor.',
  'Evita encadenar caladas muy largas para conservar la resistencia en buen estado.',
  'Recarga la batería cuando baje la intensidad de vapor.',
  'Desecha el dispositivo en un punto de recogida RAEE al final de su vida útil.',
];

const catalogue: Product[] = [
  {
    id: 'vapsolo-quads-80k',
    slug: 'vapsolo-quads-80k-2',
    name: 'Vapsolo Quads 80K',
    brand: 'VapSolo',
    category: 'desechables',
    categoryLabel: 'Vaper recargable de alta autonomía',
    puffs: 80000,
    price: 23.9,
    availableForSale: true,
    emoji: 'V',
    image: '/images/products/vapsolo/Quads 80K/grape-lychee-peach-kiwi-guava.webp',
    shortDescription: 'Hasta 80.000 caladas con batería recargable y formato premium ergonómico.',
    description:
      'Vapsolo Quads 80K está pensado para quien no quiere quedarse corto a mitad del día. Ofrece una autonomía sobresaliente, vapor consistente y un formato cómodo en mano para uso diario. Es un equipo fiable para usuarios que priorizan duración real, sabor estable y un rendimiento alto desde la primera hasta la última recarga.',
    highlights: [
      'Hasta 80.000 caladas',
      'Batería recargable',
      'Alto rendimiento de vapor',
      'Diseño ergonómico premium',
    ],
    nicotineMgMl: 20,
    ingredients: ['Líquido con sales de nicotina (2%)', 'Base PG/VG', 'Aromas alimentarios', 'Batería integrada recargable'],
    usageInstructions: DISPOSABLE_USAGE,
    adverseEffects: STANDARD_ADVERSE,
    healthWarning: STANDARD_WARNING,
    ageRestriction: '+18',
    countryOfOrigin: 'China',
    certifications: ['CE', 'RoHS'],
    batchTraceability: 'Codigo de lote en base y embalaje del producto.',
    variants: [
      {
        label: 'Sabor',
        value: 'Grape Burst + Lychee Ice + Peach Berry + Kiwi Passionfruit Guava',
        image: '/images/products/vapsolo/Quads 80K/grape-lychee-peach-kiwi-guava.webp',
      },
      {
        label: 'Sabor',
        value: 'Happy Ranch + Sex On The Beach + Watermelon Coconut + Cherry Berry',
        image: '/images/products/vapsolo/Quads 80K/11HappyRanchSexOnTheBeachWatermelonCoconutCherryBerry.webp',
      },
    ],
    metaTitle: 'Vapsolo Quads 80K - Vaper 80.000 caladas premium recargable',
    metaDescription:
      'Vapsolo Quads 80K ofrece hasta 80.000 caladas con batería recargable, vapor intenso y diseño premium. Ideal si buscas autonomía real y rendimiento constante.',
    seoKeywords: ['vapsolo 80k', 'vape 80000 caladas', 'vape premium recargable'],
    faq: [
      {
        question: '¿Cuánto dura Vapsolo Quads 80K en uso normal?',
        answer:
          'Depende del ritmo de consumo, pero está orientado a usuarios que buscan muchos días de autonomía antes de reemplazar el dispositivo.',
      },
      {
        question: '¿El rendimiento baja con el paso de las recargas?',
        answer:
          'Mantiene buena estabilidad si se recarga a tiempo y se evitan caladas excesivamente largas de forma continuada.',
      },
    ],
  },
  {
    id: 'vapsolo-triple-60k',
    slug: 'vapsolo-triple-60k-2',
    name: 'Vapsolo Triple 60K',
    brand: 'VapSolo',
    category: 'desechables',
    categoryLabel: 'Vaper recargable multisabor',
    puffs: 60000,
    price: 21.9,
    availableForSale: true,
    emoji: 'V',
    image: '/images/products/vapsolo/Triple 60K/lemon-lime-strawberry-watermelon-blueberry-ice.webp',
    shortDescription: 'Hasta 60.000 caladas con triple sistema de sabor y batería recargable.',
    description:
      'Vapsolo Triple 60K combina autonomía y variedad en un único dispositivo. Su sistema de triple sabor permite cambiar de perfil según el momento sin cargar varios vapers encima. Es una opción muy práctica para usuarios que quieren versatilidad, buena producción de vapor y uso cómodo durante todo el día.',
    highlights: ['Hasta 60.000 caladas', 'Triple sistema de sabor', 'Diseño compacto', 'Batería recargable'],
    nicotineMgMl: 20,
    ingredients: ['Líquido con sales de nicotina (2%)', 'Base PG/VG', 'Aromas alimentarios en triple cámara', 'Batería integrada recargable'],
    usageInstructions: DISPOSABLE_USAGE,
    adverseEffects: STANDARD_ADVERSE,
    healthWarning: STANDARD_WARNING,
    ageRestriction: '+18',
    countryOfOrigin: 'China',
    certifications: ['CE', 'RoHS'],
    batchTraceability: 'Codigo de lote en base y embalaje del producto.',
    variants: [
      {
        label: 'Sabor',
        value: 'Lemon Lime + Strawberry Watermelon + Blueberry Ice',
        image: '/images/products/vapsolo/Triple 60K/lemon-lime-strawberry-watermelon-blueberry-ice.webp',
      },
      {
        label: 'Sabor',
        value: 'Strawberry Kiwi + Love 66 + Cherry Cola',
        image: '/images/products/vapsolo/Triple 60K/strawberry-kiwi-love66-cherry-cola.webp',
      },
    ],
    metaTitle: 'Vapsolo Triple 60K - Vaper triple sabor 60.000 caladas',
    metaDescription:
      'Disfruta de tres perfiles de sabor en un solo dispositivo. Vapsolo Triple 60K ofrece autonomía alta, batería recargable y vapor intenso.',
    seoKeywords: ['vapsolo triple 60k', 'vape triple sabor', 'vape 60000 caladas'],
    faq: [
      {
        question: '¿Cómo se aprovecha mejor el triple sabor?',
        answer:
          'Alterna sabores por tramos de uso para no saturar el paladar y mantener una sensación más limpia durante el día.',
      },
      {
        question: '¿Es fácil de transportar?',
        answer:
          'Si. Su formato compacto cabe en bolsillo sin perder buena autonomía ni pegada de vapor.',
      },
    ],
  },
  {
    id: 'vapsolo-mars-50k',
    slug: 'vapsolo-mars-50k',
    name: 'Vapsolo Mars 40K-50K',
    brand: 'VapSolo',
    category: 'desechables',
    categoryLabel: 'Vaper recargable premium',
    puffs: 50000,
    price: 19.9,
    availableForSale: true,
    emoji: 'V',
    image: '/images/products/vapsolo/mars50k/2BlackeiceDragonfruit-600x600.webp',
    shortDescription: 'Hasta 50.000 caladas, diseño premium y sabor estable durante todo el uso.',
    description:
      'Vapsolo Mars está orientado a quien quiere un vaper equilibrado: buen diseño, gran estabilidad de sabor y una autonomía que aguanta un uso frecuente. Es una elección segura para usuarios que valoran constancia por encima de promesas vacías y quieren una experiencia fluida de principio a fin.',
    highlights: [
      'Hasta 50.000 caladas',
      'Diseño premium',
      'Gran estabilidad de sabor',
      'Batería de larga duración',
    ],
    nicotineMgMl: 20,
    ingredients: ['Líquido con sales de nicotina', 'Base PG/VG', 'Aromas alimentarios', 'Batería integrada recargable'],
    usageInstructions: DISPOSABLE_USAGE,
    adverseEffects: STANDARD_ADVERSE,
    healthWarning: STANDARD_WARNING,
    ageRestriction: '+18',
    countryOfOrigin: 'China',
    certifications: ['CE', 'RoHS'],
    batchTraceability: 'Codigo de lote en base y embalaje del producto.',
    variants: [
      {
        label: 'Sabor',
        value: 'Black Ice Dragonfruit',
        image: '/images/products/vapsolo/mars50k/2BlackeiceDragonfruit-600x600.webp',
      },
      {
        label: 'Sabor',
        value: 'Lemon Lime',
        image: '/images/products/vapsolo/mars50k/6lemonlime-600x600.webp',
      },
      {
        label: 'Sabor',
        value: 'Kiwi Passionfruit',
        image: '/images/products/vapsolo/mars50k/13kiwipassionfruit-600x600.webp',
      },
    ],
    metaTitle: 'Vapsolo Mars 50K - Vaper premium 50.000 caladas',
    metaDescription:
      'Vapsolo Mars ofrece hasta 50.000 caladas, autonomía fiable y gran estabilidad de sabor en un formato premium cómodo y elegante.',
    seoKeywords: ['vapsolo mars 50k', 'vape 50000 caladas premium'],
    faq: [
      {
        question: '¿Para qué tipo de usuario encaja Mars 50K?',
        answer: 'Para quien busca un punto medio entre autonomía alta, formato cuidado y sabor estable en uso continuado.',
      },
      {
        question: '¿El diseño premium afecta al agarre?',
        answer: 'Sí, su ergonomía mejora la sujeción y hace el uso diario más cómodo.',
      },
    ],
  },
  {
    id: 'bang-blaze-bc-5000',
    slug: 'bang-blaze-bc-5000',
    supplierReference: 'Bang001',
    name: 'Bang Blaze BC 5000',
    brand: 'Bang',
    category: 'desechables',
    categoryLabel: 'Vaper desechable recargable',
    puffs: 5000,
    price: 9.9,
    priceIsTemporary: true,
    availableForSale: false,
    availabilityMessage: 'Precio provisional: pendiente de validar stock y precio definitivo antes de activar la venta.',
    emoji: 'B',
    image: '/images/products/catalog/bang-blaze-bc-5000.jpg',
    shortDescription: 'Un Bang ligero y directo, con hasta 5.000 caladas, batería recargable y 12 sabores disponibles.',
    description:
      'Bang Blaze BC 5000 apuesta por un formato ligero de aproximadamente 52 g que resulta cómodo para llevar durante el día. Combina hasta 5.000 caladas con una batería recargable de 650 mAh y puerto USB-C, mientras su resistencia mesh de 1,2 Ω se ocupa de la entrega de vapor. Sus doce sabores permiten elegir entre perfiles frutales, frescos y dulces sin renunciar a un dispositivo sencillo y compacto.',
    highlights: [
      'Hasta 5.000 caladas',
      'Batería recargable de 650 mAh',
      'Puerto de carga USB-C',
      'Resistencia mesh de 1,2 Ω',
      'Peso aproximado de 52 g',
    ],
    nicotineMgMl: 20,
    capacityMl: 13,
    ingredients: [
      'E-líquido con sales de nicotina al 2 % (20 mg/ml)',
      'Perfil aromático correspondiente a la variante seleccionada',
      'Batería integrada recargable de 650 mAh',
      'Resistencia mesh de 1,2 Ω',
    ],
    usageInstructions: DISPOSABLE_USAGE,
    adverseEffects: STANDARD_ADVERSE,
    healthWarning: STANDARD_WARNING,
    ageRestriction: '+18',
    countryOfOrigin: 'China',
    certifications: [],
    batchTraceability: 'Código de lote indicado en el etiquetado del producto y en su embalaje.',
    variants: [
      { label: 'Sabor', value: 'Strawberry Mango' },
      { label: 'Sabor', value: 'Strawberry Ice Cream' },
      { label: 'Sabor', value: 'Watermelon Ice' },
      { label: 'Sabor', value: 'Blueberry Raspberry Ice' },
      { label: 'Sabor', value: 'Strawberry Kiwi' },
      { label: 'Sabor', value: 'Energy Drink' },
      { label: 'Sabor', value: 'Peach Mango Watermelon' },
      { label: 'Sabor', value: 'Blueberry Ice' },
      { label: 'Sabor', value: 'Strawberry Banana' },
      { label: 'Sabor', value: 'Kiwi Passion Fruit Guava' },
      { label: 'Sabor', value: 'Mixed Fruit' },
      { label: 'Sabor', value: 'Strawberry Watermelon' },
    ],
    metaTitle: 'Bang Blaze BC 5000 - Vaper recargable de 5.000 caladas',
    metaDescription:
      'Conoce Bang Blaze BC 5000: hasta 5.000 caladas, batería de 650 mAh, carga USB-C y resistencia mesh de 1,2 Ω.',
    seoKeywords: ['bang blaze bc 5000', 'bang 5000 caladas', 'vaper bang recargable'],
    faq: [
      {
        question: '¿Qué autonomía ofrece Bang Blaze BC 5000?',
        answer:
          'El fabricante declara hasta 5.000 caladas. La duración real depende de la intensidad y frecuencia de cada inhalación.',
      },
      {
        question: '¿Se puede recargar la batería?',
        answer: 'Sí. La ficha técnica indica una batería de 650 mAh y un puerto de carga USB-C.',
      },
    ],
  },
  {
    id: 'bang-blaze-140k',
    slug: 'bang-blaze-140k',
    supplierReference: 'Bang075',
    name: 'Bang Blaze 140K',
    brand: 'Bang',
    category: 'desechables',
    categoryLabel: 'Vaper recargable ultra autonomía',
    puffs: 140000,
    price: 29.9,
    availableForSale: true,
    emoji: 'V',
    image: '/images/products/catalog/bang-blaze-140k.png',
    shortDescription: 'Hasta 140.000 caladas, batería recargable y gran capacidad para uso intensivo.',
    description:
      'Bang Blaze 140K juega en otra liga cuando hablamos de autonomía. Está pensado para usuarios intensivos que quieren olvidarse de cambiar de dispositivo constantemente. Combina capacidad muy alta, sabor firme y una entrega de vapor potente, manteniendo una experiencia consistente durante todo su ciclo de vida.',
    highlights: [
      'Hasta 140.000 caladas',
      'Batería recargable',
      'Gran capacidad de líquido',
      'Diseño premium',
    ],
    nicotineMgMl: 20,
    ingredients: ['Líquido con sales de nicotina', 'Base PG/VG', 'Aromas alimentarios', 'Batería integrada recargable'],
    usageInstructions: DISPOSABLE_USAGE,
    adverseEffects: STANDARD_ADVERSE,
    healthWarning: STANDARD_WARNING,
    ageRestriction: '+18',
    countryOfOrigin: 'China',
    certifications: ['CE', 'RoHS'],
    batchTraceability: 'Codigo de lote en base y embalaje del producto.',
    variants: [
      {
        label: 'Sabor',
        value: 'Blueberry Ice + Peach Mango + Watermelon Love66',
        image: '/images/products/bang/Blaze 140K/Bang-Blaze-140K-Vape-3-in-1-Flavors-140000-Puffs-Disposable-Vape-Blueberry-Ice-Peach-Mango-Watermelon-Love66-350x350.webp',
      },
      {
        label: 'Sabor',
        value: 'Lemon Lime + Aloe Mango Melon + Watermelon Dragon Fruit',
        image: '/images/products/bang/Blaze 140K/Bang-Blaze-140K-Vape-3-in-1-Flavors-140000-Puffs-Disposable-Vape-Lemon-Lime-Aloe-Mango-Melon-Watermelon-Dragon-Fruit-350x350.webp',
      },
      {
        label: 'Sabor',
        value: 'Strawberry Kiwi + Watermelon Bubblegum + Blueberry Cherry',
        image: '/images/products/bang/Blaze 140K/Bang-Blaze-140K-Vape-3-in-1-Flavors-140000-Puffs-Disposable-Vape-Strawberry-Kiwi-Watermelon-Bubblegum-Blueberry-Cherry-350x350.webp',
      },
    ],
    metaTitle: 'Bang Blaze 140K - Vaper 140.000 caladas alta capacidad',
    metaDescription:
      'Bang Blaze 140K destaca por su autonomía extrema, batería recargable y vapor intenso. Una opción premium para usuarios que priorizan duración.',
    seoKeywords: ['bang 140k', 'vape 140000 caladas', 'vape alta capacidad'],
    faq: [
      {
        question: '¿Bang Blaze 140K es recomendable para uso diario intenso?',
        answer: 'Sí, está precisamente orientado a usuarios que vapearon mucho y buscan un dispositivo de larga duración.',
      },
      {
        question: '¿Mantiene el sabor en uso prolongado?',
        answer: 'Su configuración está pensada para ofrecer sabor estable durante la mayor parte del ciclo de uso.',
      },
    ],
  },
  {
    id: 'bang-legend-150k-6in1',
    slug: 'bang-legend-150k-6in1',
    supplierReference: 'Bang060',
    name: 'Bang Legend 150K 6in1',
    brand: 'Bang',
    category: 'desechables',
    categoryLabel: 'Vaper multisabor con pantalla',
    puffs: 150000,
    price: 33.9,
    availableForSale: true,
    emoji: 'V',
    image: '/images/products/catalog/bang-legend-150k-6in1.png',
    shortDescription: 'Hasta 150.000 caladas, 6 sabores en 1, pantalla digital y batería recargable.',
    description:
      'Bang Legend 150K 6in1 es para quien quiere todo en el mismo equipo: autonomía extrema, selección de sabores y control visual gracias a su pantalla digital. Permite cambiar de perfil según el momento sin perder rendimiento. Si buscas comodidad premium con mucha duración, este modelo es de los más completos del catálogo.',
    highlights: ['Hasta 150.000 caladas', 'Sistema 6 sabores en 1', 'Pantalla digital', 'Batería recargable'],
    nicotineMgMl: 20,
    ingredients: ['Líquido con sales de nicotina', 'Base PG/VG', 'Aromas alimentarios en 6 cámaras', 'Batería integrada recargable'],
    usageInstructions: DISPOSABLE_USAGE,
    adverseEffects: STANDARD_ADVERSE,
    healthWarning: STANDARD_WARNING,
    ageRestriction: '+18',
    countryOfOrigin: 'China',
    certifications: ['CE', 'RoHS'],
    batchTraceability: 'Codigo de lote en base y embalaje del producto.',
    variants: [
      {
        label: 'Sabor',
        value: 'Mango Ice + Peach Mango + Watermelon + Gummy Bear',
        image: '/images/products/bang/Legend 150K 6in1/bang-legend-150k-mango-ice-peach-mango-watermelon-gummy-bear.webp',
      },
      {
        label: 'Sabor',
        value: 'Passion Fruit + Guava Lime + Passion Fruit Lemon',
        image: '/images/products/bang/Legend 150K 6in1/bang-legend-150k-passionfruit-guava-lime-lemon.webp',
      },
      {
        label: 'Sabor',
        value: 'Strawberry Ice + Strawberry Kiwi + Strawberry Lychee',
        image: '/images/products/bang/Legend 150K 6in1/bang-legend-150k-strawberry-ice-kiwi-lychee.webp',
      },
    ],
    metaTitle: 'Bang Legend 150K 6in1 - Vaper 150.000 caladas multi sabor',
    metaDescription:
      'Bang Legend 150K 6in1 ofrece 6 sabores en un solo dispositivo, pantalla digital y autonomía extrema para una experiencia premium.',
    seoKeywords: ['bang legend 150k', 'vape 6 sabores', 'vape 150000 caladas'],
    faq: [
      {
        question: '¿Qué ventaja aporta la pantalla digital?',
        answer: 'Permite controlar el estado del dispositivo de forma rápida y usarlo con más precisión en el día a día.',
      },
      {
        question: '¿El sistema 6in1 cambia realmente la experiencia?',
        answer: 'Sí, evita la monotonía porque puedes alternar perfiles de sabor sin cambiar de vaper.',
      },
    ],
  },
  {
    id: 'bang-king-60k-ice-cool',
    slug: 'bang-king-60k-ice-cool',
    supplierReference: 'Bang066',
    name: 'Bang King 60K Ice Cool',
    brand: 'Bang',
    category: 'desechables',
    categoryLabel: 'Vaper compacto efecto hielo',
    puffs: 60000,
    price: 20.9,
    availableForSale: true,
    emoji: 'V',
    image: '/images/products/catalog/bang-king-60k-ice-cool.jpg',
    shortDescription: 'Hasta 60.000 caladas con efecto hielo, batería recargable y formato compacto.',
    description:
      'Bang King 60K Ice Cool está diseñado para quienes priorizan sensaciones frescas y golpe de sabor. Su efecto hielo se mantiene con buena intensidad y su formato compacto facilita llevarlo encima todo el día. Es una opción muy buena para usuarios que buscan frescura constante sin renunciar a autonomía.',
    highlights: ['Hasta 60.000 caladas', 'Efecto hielo', 'Batería recargable', 'Diseño compacto'],
    nicotineMgMl: 20,
    ingredients: ['Líquido con sales de nicotina', 'Base PG/VG', 'Aromas alimentarios con perfil cooling', 'Batería integrada recargable'],
    usageInstructions: DISPOSABLE_USAGE,
    adverseEffects: STANDARD_ADVERSE,
    healthWarning: STANDARD_WARNING,
    ageRestriction: '+18',
    countryOfOrigin: 'China',
    certifications: ['CE', 'RoHS'],
    batchTraceability: 'Codigo de lote en base y embalaje del producto.',
    variants: [
      {
        label: 'Sabor',
        value: 'Ice Cool Mix',
        image: '/images/products/bang/King 60K Ice Cool/BANG-KING-60000-Puffs-DSK048-Ice-Cool-Low-Nicotine-Bulk-Buy-Original-Rechargeable-Disposable-Vapes-Wholesale-9.webp',
      },
      {
        label: 'Sabor',
        value: 'Grape Ice',
        image: '/images/products/bang/King 60K Ice Cool/Grape-Ice-2.webp',
      },
      {
        label: 'Sabor',
        value: 'Strawberry Watermelon',
        image: '/images/products/bang/King 60K Ice Cool/Strawberry-Watermelon-4.webp',
      },
    ],
    metaTitle: 'Bang King 60K Ice Cool - Vaper efecto hielo 60.000 caladas',
    metaDescription:
      'Bang King 60K Ice Cool combina frescura intensa, batería recargable y hasta 60.000 caladas en un formato compacto.',
    seoKeywords: ['bang ice 60k', 'vape hielo 60000 caladas'],
    faq: [
      {
        question: '¿A quién le encaja más el perfil Ice Cool?',
        answer: 'A usuarios que disfrutan sabores fríos y una sensación refrescante marcada en cada calada.',
      },
      {
        question: '¿Pierde intensidad por ser compacto?',
        answer: 'No. Mantiene una salida de vapor sólida y un sabor intenso pese a su tamaño reducido.',
      },
    ],
  },
  {
    id: 'fizzy-max-iii-60k',
    slug: 'fizzy-max-iii-60000',
    name: 'Fizzy Max III 60000',
    brand: 'Fizzy',
    category: 'desechables',
    categoryLabel: 'Vaper recargable premium',
    puffs: 60000,
    price: 19.5,
    availableForSale: true,
    emoji: 'V',
    image: '/images/products/fizzy/Max III 60000/24h-vape-fizzy-max-iii-60000-puffs-blue-razz-ice-strawberry-kiwi-sour-pineapple-orange.jpg.webp',
    shortDescription: 'Hasta 60.000 caladas con batería recargable y diseño moderno.',
    description:
      'Fizzy Max III 60000 es un dispositivo equilibrado: autonomía alta, vapor generoso y un formato moderno que resulta cómodo en uso prolongado. Es una compra segura para quien quiere rendimiento consistente sin complicarse con configuraciones. Un vaper fiable para el día a día.',
    highlights: [
      'Hasta 60.000 caladas',
      'Batería recargable',
      'Diseño moderno',
      'Alto rendimiento de vapor',
    ],
    nicotineMgMl: 20,
    ingredients: ['Líquido con sales de nicotina', 'Base PG/VG', 'Aromas alimentarios', 'Batería integrada recargable'],
    usageInstructions: DISPOSABLE_USAGE,
    adverseEffects: STANDARD_ADVERSE,
    healthWarning: STANDARD_WARNING,
    ageRestriction: '+18',
    countryOfOrigin: 'China',
    certifications: ['CE', 'RoHS'],
    batchTraceability: 'Codigo de lote en base y embalaje del producto.',
    variants: [
      {
        label: 'Sabor',
        value: 'Blue Razz Ice + Strawberry Kiwi + Sour Pineapple Orange',
        image: '/images/products/fizzy/Max III 60000/24h-vape-fizzy-max-iii-60000-puffs-blue-razz-ice-strawberry-kiwi-sour-pineapple-orange.jpg.webp',
      },
      {
        label: 'Sabor',
        value: 'Strawberry Raspberry + Mango Peach + Blueberry Watermelon',
        image: '/images/products/fizzy/Max III 60000/24h-vape-fizzy-max-iii-60000-puffs-strawberry-raspberry-mango-peach-blueberry-watermelon.jpg.webp',
      },
      {
        label: 'Sabor',
        value: 'Watermelon Ice + Strawberry Vanilla Coke + Mixed Berry',
        image: '/images/products/fizzy/Max III 60000/24h-vape-fizzy-max-iii-60000-puffs-watermelon-ice-strawberry-vanilla-coke-mixed-berry.jpg.webp',
      },
    ],
    metaTitle: 'Fizzy Max III 60K - Vaper 60.000 caladas premium',
    metaDescription:
      'Fizzy Max III 60K ofrece gran duración, batería recargable y excelente producción de vapor en un diseño moderno.',
    seoKeywords: ['fizzy 60000 caladas', 'vape fizzy premium'],
    faq: [
      {
        question: '¿Fizzy Max III es buena opción para uso diario?',
        answer: 'Sí, porque combina autonomía elevada, rendimiento estable y un formato muy cómodo para llevar encima.',
      },
      {
        question: '¿Qué destaca frente a otros modelos de 60K?',
        answer: 'Su equilibrio general entre sabor, vapor y facilidad de uso, sin penalizar comodidad.',
      },
    ],
  },
  {
    id: 'fizzy-twins-50k',
    slug: 'fizzy-twins-50000',
    name: 'Fizzy Twins 50000',
    brand: 'Fizzy',
    category: 'desechables',
    categoryLabel: 'Vaper doble sabor',
    puffs: 50000,
    price: 18.9,
    availableForSale: true,
    emoji: 'V',
    image: '/images/products/fizzy/Twins 50000/Fizzy-Twins-50000-Puffs-Disposable-Vape-strawberry-ice-fizzy-cherry.webp',
    shortDescription: 'Hasta 50.000 caladas con doble sabor y batería de larga duración.',
    description:
      'Fizzy Twins 50000 ofrece una experiencia dinámica gracias a su sistema de doble sabor. Cambiar entre perfiles evita la fatiga de paladar y hace el uso más entretenido. Con buena autonomía y manejo sencillo, es un modelo muy recomendable para quien busca variedad sin sacrificar comodidad.',
    highlights: ['Hasta 50.000 caladas', 'Doble sabor', 'Batería de larga duración'],
    nicotineMgMl: 20,
    ingredients: ['Líquido con sales de nicotina', 'Base PG/VG', 'Aromas alimentarios en doble cámara', 'Batería integrada recargable'],
    usageInstructions: DISPOSABLE_USAGE,
    adverseEffects: STANDARD_ADVERSE,
    healthWarning: STANDARD_WARNING,
    ageRestriction: '+18',
    countryOfOrigin: 'China',
    certifications: ['CE', 'RoHS'],
    batchTraceability: 'Codigo de lote en base y embalaje del producto.',
    variants: [
      {
        label: 'Sabor',
        value: 'Strawberry Ice + Fizzy Cherry',
        image: '/images/products/fizzy/Twins 50000/Fizzy-Twins-50000-Puffs-Disposable-Vape-strawberry-ice-fizzy-cherry.webp',
      },
      {
        label: 'Sabor',
        value: 'Watermelon Ice + Lemon Lime',
        image: '/images/products/fizzy/Twins 50000/Fizzy-Twins-50000-Puffs-Disposable-Vape-watermelon-ice-lemon-lime.webp',
      },
      {
        label: 'Sabor',
        value: 'Mix surtido',
        image: '/images/products/fizzy/Twins 50000/Fizzy-Twins-50000-Puffs-Disposable-Vape-Wholesale.webp',
      },
    ],
    metaTitle: 'Fizzy Twins 50K - Vaper doble sabor 50.000 caladas',
    metaDescription:
      'Fizzy Twins 50K permite alternar dos sabores en un mismo dispositivo, con larga duración y vapor de buena intensidad.',
    seoKeywords: ['fizzy twins 50k', 'vape doble sabor'],
    faq: [
      {
        question: '¿Qué aporta el modo doble sabor?',
        answer: 'Te permite cambiar de perfil sobre la marcha y mantener la experiencia más variada durante todo el día.',
      },
      {
        question: '¿Es un dispositivo fácil de usar?',
        answer: 'Sí. Está pensado para uso directo, sin menús ni ajustes complicados.',
      },
    ],
  },
  {
    id: 'waspe-6in1-150k',
    slug: 'waspe-6in1-150k',
    supplierReference: 'WASPE005',
    name: 'Waspe 6in1 150K',
    brand: 'Waspe',
    category: 'desechables',
    categoryLabel: 'Vaper premium multisabor',
    puffs: 150000,
    price: 32.9,
    availableForSale: true,
    emoji: 'V',
    image: '/images/products/catalog/waspe-6in1-150k.jpg',
    shortDescription: 'Hasta 150.000 caladas con 6 sabores integrados y batería recargable.',
    description:
      'Waspe 6in1 150K es una de las opciones más completas para quien busca autonomía máxima y variedad real de uso. Integra seis perfiles de sabor para adaptarse a diferentes momentos del día y mantiene un rendimiento alto de forma consistente. Es un modelo premium pensado para usuarios que exigen duración y versatilidad en el mismo dispositivo.',
    highlights: [
      'Hasta 150.000 caladas',
      '6 sabores integrados',
      'Batería recargable',
      'Diseño premium',
    ],
    nicotineMgMl: 20,
    nicotineOptionsMgMl: [0, 20],
    capacityMl: 64,
    ingredients: ['Líquido con sales de nicotina', 'Base PG/VG', 'Aromas alimentarios en 6 cámaras', 'Batería integrada recargable'],
    usageInstructions: DISPOSABLE_USAGE,
    adverseEffects: STANDARD_ADVERSE,
    healthWarning: STANDARD_WARNING,
    ageRestriction: '+18',
    countryOfOrigin: 'China',
    certifications: ['CE', 'RoHS'],
    batchTraceability: 'Codigo de lote en base y embalaje del producto.',
    variants: [
      {
        label: 'Sabor',
        value: 'Mixed Berries + Black Dragon Ice + Strawberry Cherry Cola',
        image: '/images/products/waspe/6in1 150K/waspe-150k-mixed-berries-black-dragon-strawberry-cola.webp',
      },
      {
        label: 'Sabor',
        value: 'Strawberry Watermelon + Juicy Peach Ice + Triple Berry Ice',
        image: '/images/products/waspe/6in1 150K/waspe-150k-strawberry-watermelon-peach-triple-berry.webp',
      },
      {
        label: 'Sabor',
        value: 'Watermelon Ice + Lemon Lime + Lemon Peach',
        image: '/images/products/waspe/6in1 150K/Waspe-150K-Puff-6-in-1-Adjust-Vape-Disposable-Big-Puff-Multi-Flavor-Monkey-Vape-with-Watermelon-Ice-Lemon-Lime-Lemon-Peach-Flavor-in-EU-Vape-Wholesale.webp',
      },
    ],
    metaTitle: 'Waspe 150K 6in1 - Vaper 150.000 caladas multi sabor',
    metaDescription:
      'Waspe 150K 6in1 ofrece seis sabores y una autonomía extrema en un vaper recargable premium pensado para uso intensivo.',
    seoKeywords: ['waspe 150k', 'vape 150000 caladas', 'vape multi sabor'],
    faq: [
      {
        question: '¿Waspe 6in1 150K merece la pena para usuarios intensivos?',
        answer: 'Sí, está orientado justo a quienes necesitan mucha autonomía y variedad de sabor sin cambiar de dispositivo.',
      },
      {
        question: '¿Los seis sabores ayudan a no cansarse?',
        answer: 'Exacto. Poder alternar reduce la monotonía y mejora la experiencia en uso prolongado.',
      },
    ],
  },
  {
    id: 'waspe-40k-2',
    slug: 'waspe-40000-2',
    supplierReference: 'WASPE001',
    name: 'Waspe 40000',
    brand: 'Waspe',
    category: 'desechables',
    categoryLabel: 'Vaper compacto alto rendimiento',
    puffs: 40000,
    price: 16.9,
    availableForSale: true,
    emoji: 'V',
    image: '/images/products/catalog/waspe-40k-2.jpg',
    shortDescription: 'Hasta 40.000 caladas con formato compacto y rendimiento de vapor constante.',
    description:
      'Waspe 40000 2% está pensado para quienes prefieren un formato ligero, manejable y con pegada. Ofrece una buena producción de vapor y una duración muy competitiva dentro de su tamaño. Es una opción práctica para uso diario cuando buscas equilibrio entre comodidad y rendimiento.',
    highlights: ['Hasta 40.000 caladas', 'Diseño compacto', 'Alto rendimiento'],
    nicotineMgMl: 20,
    nicotineOptionsMgMl: [0, 20],
    capacityMl: 36,
    ingredients: ['Líquido con sales de nicotina (2%)', 'Base PG/VG', 'Aromas alimentarios', 'Batería integrada recargable'],
    usageInstructions: DISPOSABLE_USAGE,
    adverseEffects: STANDARD_ADVERSE,
    healthWarning: STANDARD_WARNING,
    ageRestriction: '+18',
    countryOfOrigin: 'China',
    certifications: ['CE', 'RoHS'],
    batchTraceability: 'Codigo de lote en base y embalaje del producto.',
    variants: [
      {
        label: 'Sabor',
        value: 'Blue Razz Lemonade + Watermelon Bubblegum',
        image: '/images/products/waspe/40000 2%/24h-vape-waspe-40000-puffs-blue-razz-lemonade-watermelon-bubblegum.jpg.webp',
      },
      {
        label: 'Sabor',
        value: 'Strawberry Cherry Kiwi Passion Fruit',
        image: '/images/products/waspe/40000 2%/24h-vape-waspe-40000-puffs-strawberry-cherry-kiwi-passion-fruit.jpg.webp',
      },
      {
        label: 'Sabor',
        value: 'Watermelon Ice + Lemon Lime',
        image: '/images/products/waspe/40000 2%/24h-vape-waspe-40000-puffs-watermelon-ice-lemon-lime.jpg.webp',
      },
    ],
    metaTitle: 'Waspe 40K - Vaper 40.000 caladas compacto',
    metaDescription:
      'Waspe 40K combina formato compacto, vapor intenso y buena autonomía para un uso cómodo y fiable en cualquier momento.',
    seoKeywords: ['waspe 40k vape'],
    faq: [
      {
        question: '¿Waspe 40K es cómodo para llevar fuera de casa?',
        answer: 'Sí. Su tamaño compacto lo hace muy práctico para bolsillo o mochila.',
      },
      {
        question: '¿Compensa frente a modelos más grandes?',
        answer: 'Si buscas equilibrio entre comodidad y rendimiento, es una alternativa muy sólida.',
      },
    ],
  },
  {
    id: 'zooy-triple-70k',
    slug: 'zooy-triple-70000',
    supplierReference: 'ZOOY023',
    name: 'Zooy Triple 70000',
    brand: 'Zooy',
    category: 'desechables',
    categoryLabel: 'Vaper triple sabor recargable',
    puffs: 70000,
    price: 22.5,
    availableForSale: true,
    emoji: 'V',
    image: '/images/products/catalog/zooy-triple-70k.jpg',
    shortDescription: 'Hasta 70.000 caladas con triple sabor, batería recargable y diseño moderno.',
    description:
      'Zooy Triple 70000 está diseñado para usuarios que quieren variedad real de sabor sin renunciar a autonomía. Su configuración triple facilita cambiar de perfil cuando lo necesitas y su formato moderno mantiene un uso cómodo y estable. Muy buena opción para quienes buscan dinamismo en cada sesión.',
    highlights: ['Hasta 70.000 caladas', 'Triple sabor', 'Batería recargable', 'Diseño moderno'],
    nicotineMgMl: 20,
    ingredients: ['Líquido con sales de nicotina', 'Base PG/VG', 'Aromas alimentarios en triple cámara', 'Batería integrada recargable'],
    usageInstructions: DISPOSABLE_USAGE,
    adverseEffects: STANDARD_ADVERSE,
    healthWarning: STANDARD_WARNING,
    ageRestriction: '+18',
    countryOfOrigin: 'China',
    certifications: ['CE', 'RoHS'],
    batchTraceability: 'Codigo de lote en base y embalaje del producto.',
    variants: [
      {
        label: 'Sabor',
        value: 'Mr. Blue + Strawberry Banana + Lemon Lime',
        image: '/images/products/zooy/Triple 70000/24h-vape-zooy-trlple-70000-puffs-mr.-blue-strawberry-banana-lemon-lime.jpg.webp',
      },
      {
        label: 'Sabor',
        value: 'Strawberry Watermelon + Blueberry Ice + Love 666',
        image: '/images/products/zooy/Triple 70000/24h-vape-zooy-trlple-70000-puffs-strawberry-watermelon-blueberry-ice-love-666.jpg.webp',
      },
      {
        label: 'Sabor',
        value: 'Watermelon + Lemon Lime + Cherry Cola',
        image: '/images/products/zooy/Triple 70000/24h-vape-zooy-trlple-70000-puffs-watermelon-lemon-lime-cherry-cola.jpg.webp',
      },
    ],
    metaTitle: 'Zooy Triple 70K - Vaper triple sabor 70.000 caladas',
    metaDescription:
      'Zooy Triple 70K combina variedad de sabores, batería recargable y larga duración para una experiencia de vapeo versátil.',
    seoKeywords: ['zooy 70000', 'vape triple sabor'],
    faq: [
      {
        question: '¿Zooy Triple es buena opción si me aburro de un solo sabor?',
        answer: 'Sí, su sistema triple permite alternar perfiles y mantener la experiencia mucho más entretenida.',
      },
      {
        question: '¿Aguanta un uso frecuente?',
        answer: 'Sí, está diseñado para ofrecer autonomía amplia y rendimiento estable.',
      },
    ],
  },
  {
    id: 'zooy-zero-45k',
    slug: 'zooy-zero-45000',
    supplierReference: 'ZOOY019',
    name: 'Zooy Zero 45000',
    brand: 'Zooy',
    category: 'desechables',
    categoryLabel: 'Vaper premium de larga duración',
    puffs: 45000,
    price: 18.5,
    availableForSale: true,
    emoji: 'V',
    image: '/images/products/catalog/zooy-zero-45k.jpg',
    shortDescription: 'Hasta 45.000 caladas con diseño premium y batería de larga duración.',
    description:
      'Zooy Zero 45000 destaca por su equilibrio: se siente premium en mano, entrega un sabor limpio y mantiene buena autonomía para uso continuo. Es una alternativa muy acertada si quieres un vaper cómodo, fiable y con una experiencia estable en cada calada.',
    highlights: ['Hasta 45.000 caladas', 'Diseño premium', 'Batería de larga duración'],
    nicotineMgMl: 20,
    ingredients: ['Líquido con sales de nicotina', 'Base PG/VG', 'Aromas alimentarios', 'Batería integrada recargable'],
    usageInstructions: DISPOSABLE_USAGE,
    adverseEffects: STANDARD_ADVERSE,
    healthWarning: STANDARD_WARNING,
    ageRestriction: '+18',
    countryOfOrigin: 'China',
    certifications: ['CE', 'RoHS'],
    batchTraceability: 'Codigo de lote en base y embalaje del producto.',
    variants: [
      {
        label: 'Sabor',
        value: 'Blueberry Ice',
        image: '/images/products/zooy/Zero 45000/24h-vape-zooy-zero-45000-45k-puffs-bluberry-ice.jpg.webp',
      },
      {
        label: 'Sabor',
        value: 'Blueberry Watermelon',
        image: '/images/products/zooy/Zero 45000/24h-vape-zooy-zero-45000-45k-puffs-blueberry-watermelon.jpg.webp',
      },
      {
        label: 'Sabor',
        value: 'Watermelon Ice',
        image: '/images/products/zooy/Zero 45000/24h-vape-zooy-zero-45000-45k-puffs-watermelon-ice.jpg.webp',
      },
    ],
    metaTitle: 'Zooy Zero 45K - Vaper 45.000 caladas premium',
    metaDescription:
      'Zooy Zero 45K ofrece rendimiento premium, autonomía estable y una experiencia de sabor constante en formato elegante.',
    seoKeywords: ['zooy 45000 vape'],
    faq: [
      {
        question: '¿Qué tipo de usuario suele elegir Zooy Zero 45K?',
        answer: 'Quien busca un modelo equilibrado, con buen acabado y rendimiento fiable para uso diario.',
      },
      {
        question: '¿El sabor se mantiene estable?',
        answer: 'Sí, está diseñado para conservar buen nivel de sabor durante la mayor parte del ciclo de uso.',
      },
    ],
  },
  {
    id: 'zooy-shisha-50k',
    slug: 'zooy-shisha-50000',
    supplierReference: 'ZOOY020',
    name: 'Zooy Shisha 50000',
    brand: 'Zooy',
    category: 'desechables',
    categoryLabel: 'Vaper estilo shisha',
    puffs: 50000,
    price: 19.5,
    availableForSale: true,
    emoji: 'V',
    image: '/images/products/catalog/zooy-shisha-50k.jpg',
    shortDescription: 'Hasta 50.000 caladas con estilo shisha y vapor denso.',
    description:
      'Zooy Shisha 50000 está hecho para amantes del vapeo tipo shisha: calada amplia, sensación densa y una producción de vapor que se nota desde el primer uso. Mantiene una autonomía muy competitiva y una experiencia intensa para quienes buscan mayor presencia en cada inhalación.',
    highlights: ['Hasta 50.000 caladas', 'Estilo shisha', 'Vapor denso'],
    nicotineMgMl: 20,
    ingredients: ['Líquido con sales de nicotina', 'Base PG/VG', 'Aromas alimentarios estilo shisha', 'Batería integrada recargable'],
    usageInstructions: DISPOSABLE_USAGE,
    adverseEffects: STANDARD_ADVERSE,
    healthWarning: STANDARD_WARNING,
    ageRestriction: '+18',
    countryOfOrigin: 'China',
    certifications: ['CE', 'RoHS'],
    batchTraceability: 'Codigo de lote en base y embalaje del producto.',
    variants: [
      {
        label: 'Sabor',
        value: 'Blueberry Ice',
        image: '/images/products/zooy/Shisha 50000/24hvape-zooy-shisha-50000-50k-puffs-blueberry-ice.jpg.webp',
      },
      {
        label: 'Sabor',
        value: 'Cherry Strawberry Raspberry',
        image: '/images/products/zooy/Shisha 50000/24hvape-zooy-shisha-50000-50k-puffs-cherry-strawberry-raspberry.jpg.webp',
      },
      {
        label: 'Sabor',
        value: 'Strawberry Watermelon',
        image: '/images/products/zooy/Shisha 50000/24hvape-zooy-shisha-50000-50k-puffs-strawberry-watermelon.jpg.webp',
      },
    ],
    metaTitle: 'Zooy Shisha 50K - Vaper estilo shisha 50.000 caladas',
    metaDescription:
      'Zooy Shisha 50K ofrece vapor denso, perfil estilo shisha y larga duración para quienes buscan una experiencia más intensa.',
    seoKeywords: ['zooy shisha 50000'],
    faq: [
      {
        question: '¿Qué diferencia a Zooy Shisha de un vaper convencional?',
        answer: 'Su perfil está orientado a una calada más amplia y una sensación de vapor más densa, estilo shisha.',
      },
      {
        question: '¿Es adecuado para sesiones largas?',
        answer: 'Sí, combina buena capacidad de uso con rendimiento constante en producción de vapor.',
      },
    ],
  },
  {
    id: 'lost-mary-tappo-glayce',
    slug: 'lost-mary-tappo-glayce',
    name: 'Lost Mary Tappo Glayce',
    brand: 'Lost Mary',
    category: 'desechables',
    categoryLabel: 'Vaper recargable premium',
    price: 18.9,
    availableForSale: true,
    emoji: 'V',
    image: '/images/products/lost_mary/Tappo Glayce/Lemon-Lime_43126feb-0405-4546-a704-5325fd7d670a_720x.webp',
    shortDescription: 'Formato compacto con sabor limpio, calada suave y perfil premium para uso diario.',
    description:
      'Lost Mary Tappo Glayce está pensado para quien quiere un vapeo cómodo y sin complicaciones, pero con sensación de calidad en cada calada. Destaca por su perfil de sabor definido, su rendimiento estable y un formato que resulta fácil de llevar todo el día. Es una opción redonda para usuarios que priorizan equilibrio entre sabor, autonomía y comodidad.',
    highlights: ['Formato premium de bolsillo', 'Sabor estable y definido', 'Uso cómodo para diario', 'Experiencia de calada suave'],
    nicotineMgMl: 20,
    ingredients: ['Líquido con sales de nicotina', 'Base PG/VG', 'Aromas alimentarios', 'Batería integrada recargable'],
    usageInstructions: DISPOSABLE_USAGE,
    adverseEffects: STANDARD_ADVERSE,
    healthWarning: STANDARD_WARNING,
    ageRestriction: '+18',
    countryOfOrigin: 'China',
    certifications: ['CE', 'RoHS'],
    batchTraceability: 'Codigo de lote en base y embalaje del producto.',
    variants: [
      {
        label: 'Sabor',
        value: 'Lemon Lime',
        image: '/images/products/lost_mary/Tappo Glayce/Lemon-Lime_43126feb-0405-4546-a704-5325fd7d670a_720x.webp',
      },
      {
        label: 'Sabor',
        value: 'Triple Mango',
        image: '/images/products/lost_mary/Tappo Glayce/Triple-Mango_bc3ccb16-82a5-4ef4-8a20-e235ec1f8fc9_720x.webp',
      },
      {
        label: 'Sabor',
        value: 'Triple Melon',
        image: '/images/products/lost_mary/Tappo Glayce/Triple-Melon_720x.webp',
      },
    ],
    metaTitle: 'Lost Mary Tappo Glayce - Vaper premium de sabor intenso',
    metaDescription:
      'Descubre Lost Mary Tappo Glayce, un vaper compacto con sabor definido, rendimiento estable y experiencia premium para el día a día.',
    seoKeywords: ['lost mary tappo glayce', 'vaper lost mary', 'lost mary sabores'],
    faq: [
      {
        question: '¿Qué destaca de Lost Mary Tappo Glayce frente a otros modelos?',
        answer:
          'Su principal punto fuerte es el equilibrio entre comodidad de uso, sabor limpio y sensación premium en formato compacto.',
      },
      {
        question: '¿Es buena opción para uso continuo?',
        answer:
          'Si. Mantiene un rendimiento estable y una experiencia consistente en sesiones de uso frecuentes.',
      },
    ],
  },
  {
    id: 'lost-mary-tappo-pro',
    slug: 'lost-mary-tappo-pro',
    name: 'Lost Mary Tappo Pro',
    brand: 'Lost Mary',
    category: 'accesorios',
    categoryLabel: 'Cartucho prefilled',
    price: 9.9,
    availableForSale: true,
    emoji: 'V',
    image: '/images/products/lost_mary/Tappo Pro/cartucho-lost-mary-tappo-pro-lemon-lime_720x.webp',
    shortDescription: 'Cartucho Lost Mary con cambio rápido y sabores directos para mantener tu equipo siempre listo.',
    description:
      'Lost Mary Tappo Pro está orientado a usuarios que valoran practicidad y sabor en recambio rápido. Su formato de cartucho permite cambiar de perfil en segundos, sin ajustes ni complicaciones. Es una solución muy cómoda para quien quiere mantener el ritmo diario con una experiencia de sabor constante.',
    highlights: ['Recambio rápido y limpio', 'Sabores definidos', 'Uso sencillo sin configuraciones', 'Ideal para rotación de sabores'],
    nicotineMgMl: 20,
    ingredients: ['Líquido con sales de nicotina', 'Base PG/VG', 'Aromas alimentarios', 'Cartucho prefilled compatible'],
    usageInstructions: [
      'Retira el cartucho anterior con cuidado y coloca el nuevo hasta oir ajuste firme.',
      'Espera unos segundos antes de la primera calada para estabilizar el flujo de líquido.',
      'No expongas el cartucho a calor directo o luz solar prolongada.',
      'Sustituye el cartucho cuando notes pérdida clara de sabor o vapor.',
      'Desecha el cartucho en un punto de recogida adecuado al final de su uso.',
    ],
    adverseEffects: STANDARD_ADVERSE,
    healthWarning: STANDARD_WARNING,
    ageRestriction: '+18',
    countryOfOrigin: 'China',
    certifications: ['CE', 'RoHS'],
    batchTraceability: 'Codigo de lote impreso en embalaje individual del cartucho.',
    variants: [
      {
        label: 'Sabor',
        value: 'Lemon Lime',
        image: '/images/products/lost_mary/Tappo Pro/cartucho-lost-mary-tappo-pro-lemon-lime_720x.webp',
      },
      {
        label: 'Sabor',
        value: 'Watermelon',
        image: '/images/products/lost_mary/Tappo Pro/cartucho-lost-mary-tappo-pro-watermelon_1296x.webp',
      },
      {
        label: 'Sabor',
        value: 'Spearmint',
        image: '/images/products/lost_mary/Tappo Pro/lost-mary-tappo-pro-spearmint_1080x.webp',
      },
    ],
    metaTitle: 'Lost Mary Tappo Pro - Cartucho prefilled con sabor intenso',
    metaDescription:
      'Cartucho Lost Mary Tappo Pro con instalación rápida y sabores definidos para una experiencia cómoda y constante.',
    seoKeywords: ['lost mary tappo pro', 'cartucho lost mary', 'tappo pro sabores'],
    faq: [
      {
        question: '¿Lost Mary Tappo Pro es fácil de cambiar?',
        answer: 'Sí. El cartucho está pensado para recambio rápido, limpio y sin necesidad de configuraciones extra.',
      },
      {
        question: '¿Para qué tipo de usuario está recomendado?',
        answer:
          'Para quien busca una opción práctica para mantener sabor estable y cambiar de perfil de forma inmediata.',
      },
    ],
  },
  {
    id: 'lost-mary-tp800',
    slug: 'lost-mary-tp800',
    name: 'Lost Mary TP800',
    brand: 'Lost Mary',
    category: 'desechables',
    categoryLabel: 'Vaper compacto',
    price: 8.9,
    availableForSale: true,
    emoji: 'V',
    image: '/images/products/lost_mary/TP800/LOST_MARY_TP800_LEMON_LIME_540x.webp',
    shortDescription: 'Modelo compacto y directo, ideal para quienes quieren sabor claro y uso sencillo.',
    description:
      'Lost Mary TP800 es una opción enfocada a comodidad total: formato ligero, perfil de sabor nítido y uso inmediato. Es perfecto para usuarios que prefieren dispositivos compactos con una experiencia limpia y sin pasos extra. Un modelo práctico para uso diario y movilidad.',
    highlights: ['Formato muy compacto', 'Sabores definidos', 'Uso directo sin ajustes', 'Perfil ideal para diario'],
    nicotineMgMl: 20,
    ingredients: ['Líquido con sales de nicotina', 'Base PG/VG', 'Aromas alimentarios', 'Batería integrada'],
    usageInstructions: [
      'Retira los protectores del dispositivo antes del primer uso.',
      'Inhala de manera suave para mantener estabilidad en sabor y vapor.',
      'Evita golpes o exposición prolongada al sol para preservar rendimiento.',
      'Desecha el dispositivo en un punto de recogida adecuado al finalizar su vida útil.',
      'Mantener fuera del alcance de menores y mascotas.',
    ],
    adverseEffects: STANDARD_ADVERSE,
    healthWarning: STANDARD_WARNING,
    ageRestriction: '+18',
    countryOfOrigin: 'China',
    certifications: ['CE', 'RoHS'],
    batchTraceability: 'Codigo de lote en el envoltorio del dispositivo.',
    variants: [
      {
        label: 'Sabor',
        value: 'Lemon Lime',
        image: '/images/products/lost_mary/TP800/LOST_MARY_TP800_LEMON_LIME_540x.webp',
      },
      {
        label: 'Sabor',
        value: 'Pineapple',
        image: '/images/products/lost_mary/TP800/LOST_MARY_TP800_PINEAPPLE_540x.webp',
      },
      {
        label: 'Sabor',
        value: 'Strawberry',
        image: '/images/products/lost_mary/TP800/LOST_MARY_TP800_STRAWBERRY_540x.webp',
      },
    ],
    metaTitle: 'Lost Mary TP800 - Vaper compacto con sabores clásicos',
    metaDescription:
      'Lost Mary TP800 combina formato ligero, uso sencillo y sabores definidos para una experiencia cómoda en cualquier momento.',
    seoKeywords: ['lost mary tp800', 'tp800 lost mary', 'lost mary sabores'],
    faq: [
      {
        question: '¿En qué destaca Lost Mary TP800?',
        answer: 'En su formato compacto y su facilidad de uso, ideal para quien busca practicidad sin renunciar al sabor.',
      },
      {
        question: '¿Es buena opción para llevar fuera de casa?',
        answer: 'Sí. Su tamaño reducido y su uso directo lo hacen muy cómodo para movilidad diaria.',
      },
    ],
  },
  ...supplierProducts,
  ...bangSupplierProducts,
];

/**
 * Qué se vende y a qué precio.
 *
 * Con dropshipping ya no hace falta tener la mercancía comprada para ponerla a
 * la venta, así que el catálogo de vapers se abre entero: lo que antes marcaba
 * «pendiente de validación» eran 225 referencias que el proveedor sirve igual.
 * El precio sale de la tarifa por caladas (ver src/lib/pricing.ts), no se
 * escribe producto a producto.
 *
 * Fuera quedan líquidos, dispositivos y accesorios: no son vapers, y aplicarles
 * una tarifa pensada por caladas dejaría un líquido de 5,90 € puesto a 15,95.
 * Esos siguen saliendo como pendientes hasta que tengan su propio precio.
 */
const SELLABLE_CATEGORIES = new Set<Category>(['desechables']);

export const products: Product[] = catalogue.map((product) =>
  product.availableForSale || SELLABLE_CATEGORIES.has(product.category)
    ? {
        ...product,
        availableForSale: true,
        availabilityMessage: undefined,
        price: toEuros(baseUnitPriceCents(product.puffs)),
        priceIsTemporary: false,
      }
    : product,
);

export const categories: { slug: Category; label: string; description: string }[] = [
  {
    slug: 'dispositivos',
    label: 'Dispositivos',
    description: 'Cigarrillos electrónicos recargables, pods y equipos de vapeo para uso diario.',
  },
  {
    slug: 'liquidos',
    label: 'Liquidos',
    description: 'Líquidos para vapeo con diferentes perfiles de sabor y concentración de nicotina.',
  },
  {
    slug: 'desechables',
    label: 'Pods desechables',
    description: 'Vapers desechables recargables de alta autonomía y formatos multisabor.',
  },
  {
    slug: 'accesorios',
    label: 'Accesorios',
    description: 'Recambios y componentes para mantener tu dispositivo en las mejores condiciones.',
  },
];

export function getProductBySlug(slug: string) {
  return products.find((p) => p.slug === slug);
}

export function getNicotineOptions(product: Product) {
  return product.nicotineOptionsMgMl ?? [product.nicotineMgMl];
}

export function getProductsByCategory(cat?: string) {
  if (!cat || cat === 'todos') return products;
  return products.filter((p) => p.category === cat);
}

export function getProductsByBrand(brand?: string) {
  if (!brand || brand === 'todas') return products;
  return products.filter((p) => slugifyBrand(p.brand) === brand);
}



/**
 * Selección que se muestra en la portada. Es una lista explícita y en orden, no un
 * filtro: «esta semana» es una decisión comercial, no una consecuencia de los datos.
 *
 * Ahora mismo corresponde a la primera compra a Xingqiong Quantum (albarán UZY BANG
 * OKSO AIRMEZ, 190 unidades). Al reponer o cambiar de pedido, se edita esta lista.
 */
const WEEKLY_SELECTION_IDS = [
  'bang-stoll-bang-leader-520k',
  'bang-450k-15in1',
  'uzy-shisha-hookah-king-120k',
  'qqbang-120k',
  'okso-shisha-hookah-mega-50k',
  'bang-tornado-40000',
  'bang-king-crystal-30000',
  'bang-king-12000',
  'airmez-fox-2in1-80k',
];

/** Los ids que no existan se descartan en silencio: un id caduco no debe romper la portada. */
export function getWeeklySelection() {
  return WEEKLY_SELECTION_IDS.map((id) => products.find((product) => product.id === id)).filter(
    (product): product is Product => Boolean(product),
  );
}
